import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useState,
    ReactNode,
} from "react";
import { AuthUser, fetchMe, JWT_STORAGE_KEY as JWT_KEY, USER_STORAGE_KEY as USER_KEY } from "../api/strapi";

interface AuthContextValue {
    user: AuthUser | null;
    jwt: string | null;
    initializing: boolean;
    setSession: (jwt: string, user: AuthUser) => void;
    logout: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

function readStoredJwt(): string | null {
    try {
        return localStorage.getItem(JWT_KEY);
    } catch {
        return null;
    }
}

function readStoredUser(): AuthUser | null {
    try {
        const raw = localStorage.getItem(USER_KEY);
        return raw ? (JSON.parse(raw) as AuthUser) : null;
    } catch {
        return null;
    }
}

export const AuthProvider = ({ children }: { children: ReactNode }) => {
    // Hydrate synchronously from localStorage so the header never flashes
    // a logged-out state for returning users.
    const [jwt, setJwt] = useState<string | null>(readStoredJwt);
    const [user, setUser] = useState<AuthUser | null>(() =>
        readStoredJwt() ? readStoredUser() : null
    );
    const [initializing, setInitializing] = useState<boolean>(!!readStoredJwt());

    const setSession = useCallback((newJwt: string, newUser: AuthUser) => {
        try {
            localStorage.setItem(JWT_KEY, newJwt);
            localStorage.setItem(USER_KEY, JSON.stringify(newUser));
        } catch {
            // Storage unavailable (private mode) — session lives in memory only.
        }
        setJwt(newJwt);
        setUser(newUser);
    }, []);

    const logout = useCallback(() => {
        try {
            localStorage.removeItem(JWT_KEY);
            localStorage.removeItem(USER_KEY);
        } catch {
            // ignore
        }
        setJwt(null);
        setUser(null);
    }, []);

    // Validate the stored token against the API once on load; refresh the
    // cached user or clear a stale/expired session.
    useEffect(() => {
        const storedJwt = readStoredJwt();
        if (!storedJwt) {
            setInitializing(false);
            return;
        }

        let cancelled = false;
        fetchMe(storedJwt)
            .then((freshUser) => {
                if (cancelled) return;
                setUser(freshUser);
                try {
                    localStorage.setItem(USER_KEY, JSON.stringify(freshUser));
                } catch {
                    // ignore
                }
            })
            .catch((err) => {
                if (cancelled) return;
                // Only drop the session when the token is actually rejected.
                // Network hiccups shouldn't log people out.
                const status = err?.response?.status;
                if (status === 401 || status === 403) {
                    logout();
                }
            })
            .finally(() => {
                if (!cancelled) setInitializing(false);
            });

        return () => {
            cancelled = true;
        };
    }, [logout]);

    return (
        <AuthContext.Provider value={{ user, jwt, initializing, setSession, logout }}>
            {children}
        </AuthContext.Provider>
    );
};

export function useAuth(): AuthContextValue {
    const ctx = useContext(AuthContext);
    if (!ctx) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return ctx;
}
